// Single source of truth for role identifiers and their display labels — imported by every
// place a role gets rendered. Before this, three files each kept their own copy of the same
// list, and adding a role meant remembering to update all three (one of them already had a
// styling quirk the others didn't, which is exactly the kind of drift a shared file prevents).
//
// The identifiers use the product's Team/Group vocabulary rather than the 'normal'/'cat'
// storage identifiers that assignments and mode periods carry. shared/scope.js's
// MODE_ADMIN_ROLE is the one place those two vocabularies are mapped to each other; nothing
// in the UI should hardcode the pairing a second time.

export const ROLES = ['owner', 'team_admin', 'group_admin', 'asset_admin', 'pm', 'pma', 'field_staff']

/** Roles only an owner may grant — mirrors OWNER_GRANTED_ROLES in server/src/repos/users.js. */
export const OWNER_GRANTED_ROLES = ['owner', 'team_admin', 'group_admin', 'asset_admin']

export const ROLE_LABEL = {
  owner: 'Owner',
  // Named after the mode each one runs, so they follow whatever those modes are called here.
  // These two entries are the fallback for a render with no tenant vocabulary loaded yet;
  // use roleLabelsFor() (or `roleLabel` from useAuth) anywhere a session is available.
  team_admin: 'Team Mode Admin',
  group_admin: 'Group Mode Admin',
  // Not named after a mode: the register is company-wide and has nothing to do with which
  // board anybody is on, so this label does not follow the tenant's mode vocabulary.
  asset_admin: 'Asset Admin',
  pm: 'PM',
  pma: 'PMA',
  field_staff: 'Field staff',
}

/**
 * The people who can actually be put on a board: field staff who are still active.
 *
 * Both crew pickers — PM teams and Group teams — derived this with the same expression
 * written out twice. It is a rule about who is schedulable, not a detail of either screen,
 * so a change to it (a new role that can be booked, a different disabled test) should not
 * depend on someone remembering there are two copies.
 */
export const schedulableStaff = (users = []) =>
  users.filter((u) => u.roles?.includes('field_staff') && u.status !== 'disabled')

/** ROLE_LABEL with each mode admin named after this tenant's word for that mode. */
export function roleLabelsFor(teamName, groupName) {
  return {
    ...ROLE_LABEL,
    team_admin: `${teamName} Mode Admin`,
    group_admin: `${groupName} Mode Admin`,
  }
}

/**
 * What each role can actually do, for the "Learn more about roles" popup on User Management.
 *
 * Written as sentences a person can act on rather than a restatement of the permission
 * matrix: the question someone arrives with is "which of these do I give the new
 * dispatcher", not "enumerate the rights of pma". The authoritative rules are in
 * shared/scope.js — if these two ever disagree, that file is right and this one is a bug.
 */
export function roleGuideFor(teamName, groupName) {
  const labels = roleLabelsFor(teamName, groupName)
  return [
    {
      role: 'owner',
      label: labels.owner,
      summary: 'Can do everything.',
      detail:
        `Everything both mode admins can do, plus the things only an owner can do. That is ` +
        `switching the company between ${teamName} and ${groupName} mode, branding, wall ` +
        `displays, the audit log, and granting any role, including this one.`,
    },
    {
      role: 'team_admin',
      label: labels.team_admin,
      summary: `Can do everything in ${teamName} mode.`,
      detail:
        `Runs the ${teamName} board. Can schedule anyone, set that mode's working days, job ` +
        `columns and staff visibility, and manage PM teams and assistants. Can also manage ` +
        `people and job types, which both modes share. Cannot change the ${groupName} board, ` +
        `and cannot switch which mode the company is running.`,
    },
    {
      role: 'group_admin',
      label: labels.group_admin,
      summary: `Can do everything in ${groupName} mode.`,
      detail:
        `Runs the ${groupName} board. Can create and order teams, lead a team as its ` +
        `coordinator, schedule across all of them, and set that mode's settings. Can also ` +
        `manage people and job types, which both modes share. Cannot change the ${teamName} ` +
        `board, and cannot switch which mode the company is running.`,
    },
    {
      role: 'asset_admin',
      label: labels.asset_admin,
      summary: 'Can keep the asset register.',
      detail:
        `Adds, edits and retires the things the company owns — vehicles, tools, computers, ` +
        `phones, clothing — the groups they fall into, and who usually has each one. ` +
        `Deliberately separate from the two board admins in both directions: running a ` +
        `schedule does not grant the register, and the register does not grant a board. Who ` +
        `has an asset on a PARTICULAR day is a board write, so it belongs to whoever fills ` +
        `that day's board. Everyone signed in can read the register.`,
    },
    {
      role: 'pm',
      label: labels.pm,
      summary: `Can manage a team in ${teamName} mode.`,
      detail:
        `Can be the project manager of a team and change the schedule, both for their own ` +
        `crew and for anyone they borrow. Cannot change ${teamName} mode settings, and ` +
        `cannot schedule in ${groupName} mode.`,
    },
    {
      role: 'pma',
      label: labels.pma,
      summary: 'Can assist a PM.',
      detail:
        `Works on behalf of the PMs they are assigned to, and can change the schedule the ` +
        `same way those PMs can. Nothing else. No settings, and no scheduling in ` +
        `${groupName} mode.`,
    },
    {
      role: 'field_staff',
      label: labels.field_staff,
      summary: 'Read only.',
      detail:
        `Can see the schedule but cannot change anything. How much of the board they see ` +
        `(their own jobs, their team, or all of it) is set separately for each mode under ` +
        `that mode's setup.`,
    },
  ]
}
